// src/components/Layout/MainLayout.js - Fixed workspace name display
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { useUserContext } from '../../contexts/UserContext';
import Sidebar from './Sidebar';
import './MainLayout.css';

// Chave para armazenar no localStorage
const SIDEBAR_STATE_KEY = 'sidebar_collapsed_state';
const THEME_STATE_KEY = 'dark_theme_enabled';

const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    userName, 
    user: contextUser, 
    activeAccountType, 
    hasPersonalAccount, 
    personalAccount,
    // Valores para perfis empresariais
    businessProfiles,
    activeBusinessProfileId,
    // Função de switch de conta
    handleSwitchAccountType,
    // Adicionar funções e estados para colaboradores
    isCollaborator,
    collaboratorWorkspaces,
    switchCollaboratorWorkspace,
    isWorkspaceSwitching
  } = useUserContext();
  
  // Get the active owner UID from localStorage
  const activeOwnerUid = localStorage.getItem('active_workspace_owner_uid');
  
  // Inicializar o estado usando o valor armazenado no localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
      return savedState === 'true';
    } catch (error) {
      console.error('Erro ao ler do localStorage:', error);
      return false;
    }
  });

  const [darkMode, setDarkMode] = useState(() => {
    try {
      const savedTheme = localStorage.getItem(THEME_STATE_KEY);
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      return savedTheme ? savedTheme === 'true' : prefersDark;
    } catch (error) {
      console.error('Erro ao ler tema do localStorage:', error);
      return false;
    }
  });

  const [showAccountDropdown, setShowAccountDropdown] = useState(false);

  // Efeito para sincronizar o estado com o localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STATE_KEY, String(sidebarCollapsed));
    } catch (error) {
      console.error('Erro ao salvar no localStorage:', error);
    }
  }, [sidebarCollapsed]);

  // Efeito para sincronizar o tema com o localStorage e aplicá-lo ao DOM
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STATE_KEY, String(darkMode));
      
      if (darkMode) {
        document.body.classList.add('dark-theme');
      } else {
        document.body.classList.remove('dark-theme');
      }
    } catch (error) {
      console.error('Erro ao salvar tema no localStorage:', error);
    }
  }, [darkMode]);

  // Efeito para fechar o dropdown quando clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event) => {
      const userContainer = document.querySelector('.user-container');
      if (userContainer && !userContainer.contains(event.target)) {
        setShowAccountDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Efeito para garantir que o estado seja recarregado quando a rota mudar
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
      const isCollapsed = savedState === 'true';
      
      if (sidebarCollapsed !== isCollapsed) {
        setSidebarCollapsed(isCollapsed);
      }
    } catch (error) {
      console.error('Erro ao recarregar o estado da sidebar:', error);
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
  };

  const toggleDarkMode = () => {
    setDarkMode(prevMode => !prevMode);
  };

  const toggleAccountDropdown = () => {
    setShowAccountDropdown(!showAccountDropdown);
  };

  // Função de seleção de conta aprimorada para lidar com workspaces de colaborador
  const handleAccountSelect = (accountType, profileId = null, ownerUid = null) => {
    // Verifica se é um workspace de colaborador
    if (isCollaborator && profileId) {
      let targetWorkspace;
      
      // If both profileId and ownerUid are provided, find by both
      if (ownerUid) {
        targetWorkspace = collaboratorWorkspaces.find(
          w => w.id === profileId && w.ownerUid === ownerUid
        );
      } 
      // Otherwise, just use profileId
      else {
        targetWorkspace = collaboratorWorkspaces.find(w => w.id === profileId);
      }
      
      if (targetWorkspace) {
        console.log(`Switching to collaborator workspace: ${targetWorkspace.profileName || targetWorkspace.workspaceName}`);
        
        // Use a função específica para colaboradores com ambos profileId e ownerUid
        switchCollaboratorWorkspace(profileId, targetWorkspace.ownerUid);
        setShowAccountDropdown(false);
        
        // Redirecionar para o dashboard se estivermos em outra página
        if (location.pathname !== '/dashboard') {
          navigate('/dashboard');
        }
        return;
      }
    }
    
    // Para contas regulares, use o manipulador normal
    handleSwitchAccountType(accountType, profileId);
    setShowAccountDropdown(false);
    
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  // Obter o nome do perfil ativo atual
  const getActiveProfileName = () => {
    if (activeAccountType === 'personal') {
      const personalProfile = businessProfiles.find(p => p.profileType === 'personal');
      return personalProfile?.profileName || `${userName} (Pessoal)`;
    } else if (isCollaborator && activeBusinessProfileId) {
      // Para colaboradores, tentar encontrar o workspace ativo usando profileId e ownerUid
      let workspace;
      
      if (activeOwnerUid) {
        workspace = collaboratorWorkspaces.find(
          w => w.id === activeBusinessProfileId && w.ownerUid === activeOwnerUid
        );
      }
      
      if (!workspace) {
        workspace = collaboratorWorkspaces.find(w => w.id === activeBusinessProfileId);
      }
      
      return workspace?.profileName || workspace?.workspaceName || userName;
    } else {
      const activeProfile = businessProfiles.find(p => p.id === activeBusinessProfileId);
      return activeProfile?.profileName || userName;
    }
  };

  // CORREÇÃO: Filtrar perfis empresariais para não incluir perfis pessoais
  const businessOnlyProfiles = businessProfiles.filter(profile => 
    profile.profileType === 'business' || !profile.profileType
  );

  // CORREÇÃO: Obter perfil pessoal dos businessProfiles
  const personalProfile = businessProfiles.find(profile => 
    profile.profileType === 'personal'
  );

  return (
    <div className={`layout-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${darkMode ? 'dark-theme' : ''}`}>
      <Sidebar collapsed={sidebarCollapsed} darkMode={darkMode} />
      <div className="content-area">
        <header className="header">
          <div className="header-left">
            <button 
              className="toggle-sidebar-button" 
              onClick={toggleSidebar}
              aria-label={sidebarCollapsed ? "Expandir menu" : "Colapsar menu"}
            >
              <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>
          
          <div className="header-right">
            {/* Toggle de Dark Mode */}
            <div className="theme-toggle-container">
              <span className="theme-label">Tema</span>
              <div 
                className={`theme-toggle ${darkMode ? 'dark' : ''}`} 
                onClick={toggleDarkMode}
                role="switch"
                aria-checked={darkMode}
              >
                <div className="toggle-thumb">
                  
                </div>
              </div>
            </div>
            
            <div className="user-container" onClick={toggleAccountDropdown}>
              <div className="user-avatar">{getActiveProfileName().charAt(0)}</div>
              <div className="user-name-container">
                <span className="user-name">
                  {getActiveProfileName()}
                </span>
                {/* Display owner ID badge for collaborator workspaces as a separate element */}
                {isCollaborator && activeOwnerUid && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-1 py-0.5 rounded-full inline-block ml-1">
                    ID:{activeOwnerUid.substring(0, 4)}
                  </span>
                )}
              </div>
              
              {/* Ícone de seta (sempre mostrar se houver contas múltiplas) */}
              {(personalProfile || businessOnlyProfiles.length > 1 || (isCollaborator && collaboratorWorkspaces.length > 1)) && (
                <div className="dropdown-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              )}
              
              {/* Dropdown para seleção de conta (mostrar se tem conta pessoal ou múltiplos perfis) */}
              {(personalProfile || businessOnlyProfiles.length > 1 || (isCollaborator && collaboratorWorkspaces.length > 1)) && showAccountDropdown && (
                <div className="account-dropdown">
                  <div className="dropdown-header">
                    <h4 className="dropdown-title">Selecione a conta</h4>
                  </div>
                  <div className="account-options">
                    {/* Seção específica para workspaces de colaborador */}
                    {isCollaborator && collaboratorWorkspaces.length > 0 && (
                      <>
                        <div className="px-4 py-1 text-xs font-semibold text-gray-500 bg-gray-50">
                          Workspaces Compartilhados
                        </div>
                        {collaboratorWorkspaces.map(workspace => {
                          // Determine if this is the active workspace using both id and ownerUid
                          const isActive = workspace.id === activeBusinessProfileId && 
                                           workspace.ownerUid === activeOwnerUid;
                          
                          // Get clean workspace name
                          const workspaceName = workspace.profileName || workspace.workspaceName || 'Unnamed Workspace';
                          
                          return (
                            <div 
                              key={`${workspace.ownerUid}-${workspace.id}`}
                              className={`account-option ${isActive ? 'active' : ''}`}
                              onClick={() => handleAccountSelect('business', workspace.id, workspace.ownerUid)}
                            >
                              <div className="account-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              <div className="account-info">
                                <div className="account-name-container flex items-center">
                                  <span className="account-name">{workspaceName}</span>
                                  {/* Add owner ID badge as a separate element */}
                                  <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full inline-block">
                                    ID:{workspace.ownerUid.substring(0, 4)}
                                  </span>
                                </div>
                                <p className="account-type">
                                  {workspace.role === 'owner' ? 'Owner' : 'Colaborador'}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Separador se houver perfis próprios e workspaces */}
                        {(businessOnlyProfiles.length > 0 || personalProfile) && (
                          <div className="px-4 py-1 text-xs font-semibold text-gray-500 bg-gray-50 mt-2">
                            Suas Contas
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Lista apenas perfis empresariais (sem perfis pessoais) */}
                    {businessOnlyProfiles.map(profile => (
                      <div 
                        key={profile.id}
                        className={`account-option ${activeAccountType === 'business' && activeBusinessProfileId === profile.id ? 'active' : ''}`}
                        onClick={() => handleAccountSelect('business', profile.id)}
                      >
                        <div className="account-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                          </svg>
                        </div>
                        <div className="account-info">
                          <p className="account-name">{profile.profileName}</p>
                          <p className="account-type">Empresarial</p>
                        </div>
                      </div>
                    ))}
                    
                    {/* Conta pessoal (se existir) */}
                    {personalProfile && (
                      <div 
                        className={`account-option ${activeAccountType === 'personal' ? 'active' : ''}`}
                        onClick={() => handleAccountSelect('personal', personalProfile.id)}
                      >
                        <div className="account-icon personal">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <div className="account-info">
                          <p className="account-name">{personalProfile.profileName}</p>
                          <p className="account-type">Pessoal</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="dropdown-footer">
                    <a href="/settings" onClick={(e) => { e.stopPropagation(); setShowAccountDropdown(false); }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                      </svg>
                      Configurar contas
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;